/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react'
import Input from 'components/common/input/Input'
import CustomModal from 'components/common/modal/CustomModal'
import SelectComponent from 'components/common/select/SelectComponent'
import { Form, useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useDropzone } from 'react-dropzone'
import { CloudArrowUp } from 'phosphor-react'
import { minioSingleFileUpload } from 'containers/events/Api'
import { createUser } from 'containers/users/Api'
import { get } from 'services/network'
import { getAccessToken, getOrgIdFromStorage } from 'utils/common'
import { toast } from 'react-toastify'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { alertTypes } from 'constants/Common'
import * as yup from 'yup'
import 'containers/users/Users.scss'

const speakerUserValidationSchema = yup.object().shape({
  firstName: yup.string().required('Required *'),
  lastName: yup.string().required('Required *'),
  email: yup.string().email().required('Required *'),
  phone: yup.string().required('Required *'),
  password: yup.string().required('Required *'),
  organizationIds: yup
    .array()
    .of(yup.string())
    .min(1, 'Required *')
    .required('Required *'),
  designation: yup.string().required('Required *'),
  description: yup.string().required('Required *'),
  image: yup.string().required('Required *'),
})

const speakerUserInitialValues = {
  type: 'speaker',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  organizationIds: [],
  designation: '',
  description: '',
  image: '',
}

function SpeakerModal({
  handleCloseModal,
  formik,
  onSpeakerCreated,
  openSpeakerIndex,
}) {
  const currentOrganizationId = getOrgIdFromStorage() || ''
  const [organizationsList, setOrganizationsList] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchOrganizations = async () => {
      try {
        const response = await get('organization', false, null, null, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        })
        if (cancelled) return
        const payload = Array.isArray(response)
          ? response
          : response?.organizations || response?.data || []
        setOrganizationsList(payload)
      } catch (error) {
        if (!cancelled) setOrganizationsList([])
      }
    }
    fetchOrganizations()
    return () => {
      cancelled = true
    }
  }, [])

  const speakerFormik = useFormik({
    initialValues: {
      ...speakerUserInitialValues,
      organizationIds: [currentOrganizationId].filter(Boolean),
    },
    enableReinitialize: true,
    validationSchema: speakerUserValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      const fullName = `${values.firstName || ''} ${values.lastName || ''}`.trim()
      const selectedOrganizationIds = values.organizationIds || []

      const result = await createUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phone: String(values.phone),
        type: 'speaker',
        name: fullName,
        organizationId: selectedOrganizationIds.length
          ? selectedOrganizationIds
          : [currentOrganizationId].filter(Boolean),
        image: values.image,
        description: values.description,
        designation: values.designation,
        jobTitle: values.designation,
      })

      if (result?.status === 200) {
        const createdUser = result?.User || result?.data || {}
        const speakerPayload = {
          name: fullName,
          email: values.email,
          designation: values.designation,
          description: values.description,
          image: values.image,
          mobileNumber: String(values.phone),
          // eslint-disable-next-line no-underscore-dangle
          userId:
            createdUser?._id ||
            createdUser?.id ||
            createdUser?.userId ||
            '',
        }

        const speakerUserForOptions = {
          ...createdUser,
          type: 'speaker',
          firstName: values.firstName,
          lastName: values.lastName,
          name: fullName,
          email: values.email,
          phone: values.phone,
          designation: values.designation,
          description: values.description,
          image: values.image,
        }

        const speakers = formik.values.speakers || []
        const isEmptySpeaker = (speaker) =>
          !speaker?.userId && !String(speaker?.name || '').trim()

        let targetIndex = -1
        if (
          openSpeakerIndex >= 0 &&
          openSpeakerIndex < speakers.length &&
          isEmptySpeaker(speakers[openSpeakerIndex])
        ) {
          targetIndex = openSpeakerIndex
        } else {
          targetIndex = speakers.findIndex(isEmptySpeaker)
        }

        if (targetIndex >= 0) {
          const nextSpeakers = [...speakers]
          nextSpeakers[targetIndex] = {
            ...nextSpeakers[targetIndex],
            ...speakerPayload,
          }
          formik.setFieldValue('speakers', nextSpeakers)
        } else {
          formik.setFieldValue('speakers', [...speakers, speakerPayload])
        }

        if (onSpeakerCreated) {
          await onSpeakerCreated(speakerUserForOptions)
        }

        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message={result?.statusText || 'Speaker Created Successfully!'}
          />
        )
        setIsSubmitting(false)
        handleCloseModal()
        return
      }

      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error || 'Failed to create speaker'}
        />
      )
      setIsSubmitting(false)
    },
  })

  const organizationOptions = organizationsList
    .filter((item) => item.enabled !== false)
    .map((item) => ({
      value: String(item.organizationId || item.id || ''),
      label: item.org_name || item.name || item.organizationName,
      isCurrent:
        String(item.organizationId || item.id || '') ===
        String(currentOrganizationId || ''),
    }))
    .filter((item) => item.value && item.label)
    .sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent))
    .map(({ value, label, isCurrent }) => ({
      value,
      label: isCurrent ? `${label} ` : label,
    }))

  const selectedOrganizationOptions = organizationOptions.filter((item) =>
    (speakerFormik.values.organizationIds || []).includes(item.value)
  )

  const handleFileUpload = useCallback(
    async (file) => {
      const formData = new FormData()
      formData.append('file', file, 'file')
      formData.append('project', 'xler')
      formData.append('fileType', file.type)

      const result = await minioSingleFileUpload(formData, () => {})
      if (result?.message && result?.paths) {
        speakerFormik.setFieldValue('image', result.paths)
        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message={result?.message || 'Successfully!'}
          />
        )
        return
      }

      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error || 'Upload failed'}
        />
      )
    },
    [speakerFormik]
  )

  const onDrop = useCallback(
    (files) => {
      const [first] = files || []
      if (first) handleFileUpload(first)
    },
    [handleFileUpload]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/png': [],
      'image/jpeg': [],
      'image/jpg': [],
    },
    maxSize: 10 * 1024 * 1024,
  })

  return (
    <CustomModal size='md' show onHide={handleCloseModal} heading='Add Speaker'>
      <Form
        className='form-main user-create-form'
        onSubmit={speakerFormik.handleSubmit}
      >
        <div className='row'>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <div className='select-field'>
                <label htmlFor='type' className='input-label'>
                  Type *
                </label>
                <SelectComponent
                  name='type'
                  options={[{ value: 'speaker', label: 'Speaker' }]}
                  selectedValue='speaker'
                  placeholder='Speaker'
                  isDisabled
                  handleChange={() => {}}
                />
              </div>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='firstName'
                handleChange={speakerFormik.handleChange}
                placeholder='First Name'
                label='First Name *'
                value={speakerFormik.values.firstName}
              />
              <p className='error-text'>
                {speakerFormik.touched.firstName &&
                  speakerFormik.errors.firstName}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='lastName'
                handleChange={speakerFormik.handleChange}
                placeholder='Last Name'
                label='Last Name *'
                value={speakerFormik.values.lastName}
              />
              <p className='error-text'>
                {speakerFormik.touched.lastName && speakerFormik.errors.lastName}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='email'
                handleChange={speakerFormik.handleChange}
                placeholder='Email'
                label='Email *'
                value={speakerFormik.values.email}
              />
              <p className='error-text'>
                {speakerFormik.touched.email && speakerFormik.errors.email}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='password'
                handleChange={speakerFormik.handleChange}
                placeholder='Password'
                label='Password *'
                type='password'
              />
              <p className='error-text'>
                {speakerFormik.touched.password &&
                  speakerFormik.errors.password}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='phone'
                handleChange={speakerFormik.handleChange}
                placeholder='Phone Number'
                label='Phone Number *'
                value={speakerFormik.values.phone}
              />
              <p className='error-text'>
                {speakerFormik.touched.phone && speakerFormik.errors.phone}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <div className='select-field'>
                <label htmlFor='organizationIds' className='input-label'>
                  Organization *
                </label>
                <SelectComponent
                  name='organizationIds'
                  options={organizationOptions}
                  selectedValue={selectedOrganizationOptions}
                  placeholder='Select'
                  isMulti
                  handleChange={(options) =>
                    speakerFormik.setFieldValue(
                      'organizationIds',
                      (options || []).map((item) => item.value)
                    )
                  }
                />
              </div>
              <p className='error-text'>
                {speakerFormik.touched.organizationIds &&
                  speakerFormik.errors.organizationIds}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='designation'
                handleChange={speakerFormik.handleChange}
                placeholder='Speaker Designation'
                label='Speaker Designation *'
                value={speakerFormik.values.designation}
              />
              <p className='error-text'>
                {speakerFormik.touched.designation &&
                  speakerFormik.errors.designation}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <Input
                name='description'
                handleChange={(e) =>
                  speakerFormik.setFieldValue('description', e.target.value)
                }
                placeholder='Speaker Description'
                label='Speaker Description *'
                value={speakerFormik.values.description || ''}
                type='textarea'
              />
              <p className='error-text'>
                {speakerFormik.touched.description &&
                  speakerFormik.errors.description}
              </p>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='field-wrapper'>
              <span className='input-label'>Speaker Image *</span>
              <div {...getRootProps()} className='speaker-image-upload'>
                <input {...getInputProps()} />
                <CloudArrowUp size={28} />
                <p>
                  {speakerFormik.values.image ? 'Change Photo' : 'Choose Photo'}
                </p>
              </div>
              {speakerFormik.values.image ? (
                <div className='speaker-image-preview'>
                  <img src={speakerFormik.values.image} alt='Speaker' />
                </div>
              ) : null}
              <p className='error-text'>
                {speakerFormik.touched.image && speakerFormik.errors.image}
              </p>
            </div>
          </div>

          <div className='col-md-12 d-flex justify-content-end gap-4 mt-2'>
            <button
              type='button'
              className='secondary-btn record-btn'
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='primary-btn record-btn'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Speaker'}
            </button>
          </div>
        </div>
      </Form>
    </CustomModal>
  )
}

SpeakerModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  formik: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    values: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  onSpeakerCreated: PropTypes.func,
  openSpeakerIndex: PropTypes.number,
}

SpeakerModal.defaultProps = {
  onSpeakerCreated: undefined,
  openSpeakerIndex: -1,
}

export default SpeakerModal
